import { useState, useEffect, useCallback } from 'react';
import api from '../config/axiosConfig';

export interface ServerStatus {
  isServerOnline: boolean;
  lastChecked: Date | null;
  isChecking: boolean;
  error: string | null;
}

/**
 * Hook to monitor server health
 * Performs periodic health checks and detects server offline
 */
export const useServerHealth = (checkInterval: number = 30000) => {
  const [serverStatus, setServerStatus] = useState<ServerStatus>({
    isServerOnline: true,
    lastChecked: null,
    isChecking: false,
    error: null,
  });

  const checkServerHealth = useCallback(async () => {
    // Don't check if browser is offline
    if (!navigator.onLine) {
      setServerStatus(prev => ({
        ...prev,
        isServerOnline: false,
        lastChecked: new Date(),
        error: 'Browser offline',
      }));
      return;
    }

    setServerStatus(prev => ({ ...prev, isChecking: true }));

    try {
      // Try to reach health endpoint with short timeout
      const response = await api.get('/health', {
        timeout: 5000, // 5 second timeout for health check
      });

      if (response.status === 200) {
        setServerStatus({
          isServerOnline: true,
          lastChecked: new Date(),
          isChecking: false,
          error: null,
        });
      } else {
        throw new Error(`Server returned ${response.status}`);
      }
    } catch (error: any) {
      console.error('Server health check failed:', error.message);
      
      setServerStatus({
        isServerOnline: false,
        lastChecked: new Date(),
        isChecking: false,
        error: error.message || 'Server tidak dapat diakses',
      });
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkServerHealth();

    // Setup periodic checks
    const interval = setInterval(checkServerHealth, checkInterval);

    // Check on window focus (user returns to tab)
    const handleFocus = () => {
      checkServerHealth();
    };
    window.addEventListener('focus', handleFocus);

    // Check on network online event
    const handleOnline = () => {
      setTimeout(checkServerHealth, 1000); // Wait 1s after network comes back
    };
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkServerHealth, checkInterval]);

  return {
    ...serverStatus,
    recheckNow: checkServerHealth,
  };
};

export default useServerHealth;
